/**
 * InteractionManager - Handles user interactions with 3D objects.
 */
import { debugError } from '../utils/debug'

export class InteractionManager
{
  /** @type {Array} List of interactable objects */
  interactables = []

  /** @type {Function} Function to show/hide popup */
  setShowPopup = null

  /** @type {Function} Function to set popup content */
  setPopupContent = null

  /** @type {Function} Function to navigate to a scene zone */
  goToSceneZone = null

  /** @type {Function} Function to play an animation */
  playAnimation = null

  /** @type {Function} Function to play a sound */
  playSound = null

  constructor(setShowPopup, setPopupContent, goToSceneZone, playAnimation, playSound)
  {
    this.interactables = []
    this.setShowPopup = setShowPopup
    this.setPopupContent = setPopupContent
    this.goToSceneZone = goToSceneZone
    this.playAnimation = playAnimation
    this.playSound = playSound
    this.videoSubscribers = new Set()
    this.audioSubscribers = new Set()
  }

  subscribeToVideoTrigger(callback)
  {
    this.videoSubscribers.add(callback)
    return () => this.videoSubscribers.delete(callback)
  }

  notifyVideoTrigger(name)
  {
    this.videoSubscribers.forEach(cb =>
    {
      try { cb(name) }
      catch (e) { debugError('[InteractionManager] Video subscriber error:', e) }
    })
  }

  subscribeToAudioTrigger(callback)
  {
    this.audioSubscribers.add(callback)
    return () => this.audioSubscribers.delete(callback)
  }

  notifyAudioTrigger(name)
  {
    this.audioSubscribers.forEach(cb =>
    {
      try { cb(name) }
      catch (e) { debugError('[InteractionManager] Audio subscriber error:', e) }
    })
  }

  /**
   * Handle a click/tap interaction on an object.
   */
  handleInteraction = async (event, element) =>
  {
    document.body.style.cursor = 'pointer'
    event.stopPropagation()

    const userData = event.object.userData
    const type = userData.interactableType
    const data = userData.interactableData
    const hasSoundTrigger = userData.mediaTrigger === 'OnSelect'

    // Play all OnSelect animations
    const animationPromises = this.playAnimationActions(userData.OnSelectAnimations)

    // Play sound if triggered on select
    if (hasSoundTrigger)
    {
      const listener = element?.camera?.children?.find(c => c.type === 'AudioListener')
      if (listener?.context?.state === 'suspended')
      {
        await listener.context.resume()
      }

      if (userData.mediaType === 'Video')
      {
        this.notifyVideoTrigger(event.object.name)
      } else if (userData.mediaType === 'Audio' || userData.mediaType === '3DPositionalAudio')
      {
        this.notifyAudioTrigger(event.object.name)
      } else
      {
        this.playSound(event.object)
      }
    }

    await Promise.all(animationPromises)
    this.performInteractionAction(type, data)
  }

  /**
   * Handle pointer enter (hover start).
   */
  handlePointerEnter = (event, element) =>
  {
    const userData = event.object.userData
    const isClickable = userData.interactableType || (userData.OnSelectAnimations && userData.OnSelectAnimations.length > 0)
    if (isClickable)
    {
      document.body.style.cursor = 'pointer'
    }
    this.playAnimationActions(userData.OnPointerEnterAnimations)
    if (userData.mediaTrigger === 'OnPointerEnter') this.playSound(event.object)
  }

  /**
   * Handle pointer exit (hover end).
   */
  handlePointerExit = (event, element) =>
  {
    document.body.style.cursor = 'auto'
    const userData = event.object.userData
    this.playAnimationActions(userData.OnPointerExitAnimations)
    if (userData.mediaTrigger === 'OnPointerExit') this.playSound(event.object)
  }

  /** @private */
  playAnimationActions(actions)
  {
    if (!actions || !Array.isArray(actions)) return []
    return actions.map(actionName => this.playAnimation(actionName))
  }

  /** @private */
  performInteractionAction(type, data)
  {
    switch (type)
    {
      case 'Popup HTML':
        this.setShowPopup(true)
        this.setPopupContent(data)
        break
      case 'Open Link':
        window.open(data, '_blank')
        break
      case 'Go To Scene Zone':
        this.goToSceneZone(data)
        break
    }
  }
}
